import { useEffect } from 'react';
import type { AddPrinterToLibraryInput, TenantPrinterWithDetails, StorageLocation } from '@maker-toolchest/shared';
import { addPrinterToLibrarySchema } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Alert } from '@/components/Alert';
// import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import { useStorageLocations } from '@/features/storage';
import { useTier } from '@/hooks/useAuth';
import { useFormState } from '@/hooks/useFormState';
import { getErrorMessage, isValidationError, zodErrorsToFieldErrors } from '@/utils/formErrors';

interface PrinterFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddPrinterToLibraryInput) => Promise<void>;
  printer?: TenantPrinterWithDetails | null;
  loading?: boolean;
  printerId?: string; // For adding from library
  error?: Error | null; // API error to display
}

// Form data type with string dates (from HTML inputs)
type PrinterFormData = Omit<Partial<AddPrinterToLibraryInput>, 'purchaseDate'> & {
  purchaseDate?: string;
};

const defaultFormData: PrinterFormData = {
  instanceName: '',
  serialNumber: '',
  status: 'IN_SERVICE',
};

export function PrinterForm({
  open,
  onClose,
  onSubmit,
  printer,
  loading = false,
  printerId,
  error,
}: PrinterFormProps) {
  const { data: locationsData } = useStorageLocations();
  const { isBusiness } = useTier();

  const {
    formData,
    errors,
    handleChange,
    setFormData,
    setErrors,
    isEditing,
  } = useFormState<PrinterFormData>({
    defaultValues: defaultFormData,
    currentItem: printer as PrinterFormData | null,
    isOpen: open,
    error,
    itemToFormData: (p: any) => ({
      instanceName: p.instanceName || '',
      serialNumber: p.serialNumber || '',
      purchaseDate: p.purchaseDate ? new Date(p.purchaseDate).toISOString().split('T')[0] : undefined,
      purchasePrice: p.purchasePrice || undefined,
      status: p.status,
      storageLocationId: p.storageLocationId || undefined,
    }),
  });

  const locations = locationsData || [];

  // Handle printerId being passed separately (for adding from library)
  useEffect(() => {
    if (open && printerId && !printer) {
      setFormData({ ...defaultFormData, printerId });
    }
  }, [open, printerId, printer, setFormData]);

  const validate = (): boolean => {
    // Clean up empty string values before validation
    const dataToValidate: AddPrinterToLibraryInput = {
      printerId: formData.printerId!,
      instanceName: formData.instanceName || undefined,
      serialNumber: formData.serialNumber || undefined,
      purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate) : undefined,
      purchasePrice: formData.purchasePrice || undefined,
      status: formData.status || 'IN_SERVICE',
      storageLocationId: formData.storageLocationId || undefined,
    };

    // Validate with Zod schema
    const result = addPrinterToLibrarySchema.safeParse(dataToValidate);

    if (!result.success) {
      const newErrors = zodErrorsToFieldErrors(result.error);
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      // Clean up empty string values
      const cleanData: AddPrinterToLibraryInput = {
        printerId: formData.printerId!,
        instanceName: formData.instanceName || undefined,
        serialNumber: formData.serialNumber || undefined,
        purchaseDate: formData.purchaseDate ? new Date(formData.purchaseDate) : undefined,
        purchasePrice: formData.purchasePrice || undefined,
        status: formData.status || 'IN_SERVICE',
        storageLocationId: formData.storageLocationId || undefined,
      };

      await onSubmit(cleanData);
    } catch {
      // Error handled by caller
    }
  };

  const statusOptions = [
    { value: 'IN_SERVICE', label: 'In Service' },
    { value: 'IN_STORAGE', label: 'In Storage' },
    { value: 'NEEDS_MAINTENANCE', label: 'Needs Maintenance' },
    { value: 'OUT_OF_SERVICE', label: 'Out of Service' },
    { value: 'DISPOSED', label: 'Disposed' },
  ];

  const locationOptions = [
    { value: '', label: 'No location' },
    ...locations.map((loc: StorageLocation) => ({ value: loc.id, label: loc.name })),
  ];

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Printer' : 'Add Printer to Workspace'}
    >
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="flex-1 space-y-6 overflow-y-auto p-4">
          {/* API Error Banner */}
          {error && !isValidationError(error) && (
            <Alert
              variant="error"
              title="Error"
              message={getErrorMessage(error)}
            />
          )}
          {error && isValidationError(error) && (
            <Alert
              variant="error"
              title="Validation Error"
              message="Please fix the errors below"
            />
          )}

          {/* Basic Info */}
          <FormSection title="Basic Info" defaultOpen>
            <div className="space-y-4">
              <Input
                label="Instance Name"
                placeholder="e.g., Office P1S, Workshop Printer"
                value={formData.instanceName || ''}
                onChange={(e) => handleChange('instanceName', e.target.value)}
                error={errors.instanceName}
              />
              <Input
                label="Serial Number"
                placeholder="Enter serial number"
                value={formData.serialNumber || ''}
                onChange={(e) => handleChange('serialNumber', e.target.value)}
                error={errors.serialNumber}
              />
              <Select
                label="Status"
                value={formData.status || 'IN_SERVICE'}
                onChange={(e) => handleChange('status', e.target.value)}
                options={statusOptions}
                error={errors.status}
              />
              <Select
                label="Storage Location"
                value={formData.storageLocationId || ''}
                onChange={(e) => handleChange('storageLocationId', e.target.value || undefined)}
                options={locationOptions}
                error={errors.storageLocationId}
              />
            </div>
          </FormSection>

          {/* Purchase Info */}
          <FormSection title="Purchase Info">
            <div className="space-y-4">
              <Input
                label="Purchase Date"
                type="date"
                value={formData.purchaseDate || ''}
                onChange={(e) => handleChange('purchaseDate', e.target.value || undefined)}
                error={errors.purchaseDate}
              />
              {isBusiness ? (
                <Input
                  label="Purchase Price"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.purchasePrice || ''}
                  onChange={(e) => handleChange('purchasePrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                  error={errors.purchasePrice}
                />
              ) : (
                <div className="rounded-lg border border-border bg-background-subtle p-4">
                  <p className="text-sm text-foreground-muted">
                    💼 Cost tracking is available on the Business tier.{' '}
                    <a href="/settings/subscription" className="text-primary hover:underline">
                      Upgrade to track costs
                    </a>
                  </p>
                </div>
              )}
            </div>
          </FormSection>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-border bg-background-subtle px-4 py-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" loading={loading}>
            {isEditing ? 'Save Changes' : 'Add Printer'}
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
